// ---------------------------------------------------------------------------
// PropertyFilters — Chip filters derived from option metadata.properties
// ---------------------------------------------------------------------------

import { useMemo } from 'react';
import type { StepOption } from '@/api/types';
import { cn } from '@/utils/cn';
import styles from './PropertyFilters.module.css';

interface TariffProperty {
  key: string;
  label: string;
  value_id: string;
  value_name: string;
}

interface FilterGroup {
  key: string;
  label: string;
  values: Array<{ value_id: string; value_name: string }>;
}

interface PropertyFiltersProps {
  options: StepOption[];
  activeFilters: Record<string, string>;
  onFilterChange: (key: string, valueId: string) => void;
}

/** Returns property keys that have ≥2 distinct values across options. */
export function getFilterableKeys(options: StepOption[]): Set<string> {
  const counts = new Map<string, Set<string>>();
  for (const option of options) {
    const props = option.metadata['properties'] as Array<{ key: string; value_id: string }> | undefined;
    if (!props) continue;
    for (const p of props) {
      let values = counts.get(p.key);
      if (!values) {
        values = new Set();
        counts.set(p.key, values);
      }
      values.add(p.value_id);
    }
  }
  const keys = new Set<string>();
  for (const [key, values] of counts) {
    if (values.size > 1) keys.add(key);
  }
  return keys;
}

/** Filter options by active property chips using AND logic. */
export function filterByProperties(
  options: StepOption[],
  activeFilters: Record<string, string>,
): StepOption[] {
  const activeEntries = Object.entries(activeFilters);
  if (activeEntries.length === 0) return options;
  return options.filter((o) => {
    const props = o.metadata['properties'] as Array<{ key: string; value_id: string }> | undefined;
    if (!props) return false;
    return activeEntries.every(([key, valueId]) =>
      props.some((p) => p.key === key && p.value_id === valueId),
    );
  });
}

export function PropertyFilters({ options, activeFilters, onFilterChange }: PropertyFiltersProps) {
  const groups = useMemo<FilterGroup[]>(() => {
    const map = new Map<string, FilterGroup>();
    for (const option of options) {
      const props = option.metadata['properties'] as TariffProperty[] | undefined;
      if (!props) continue;
      for (const p of props) {
        let group = map.get(p.key);
        if (!group) {
          group = { key: p.key, label: p.label, values: [] };
          map.set(p.key, group);
        }
        if (!group.values.some((v) => v.value_id === p.value_id)) {
          group.values.push({ value_id: p.value_id, value_name: p.value_name });
        }
      }
    }
    return Array.from(map.values()).filter((g) => g.values.length > 1);
  }, [options]);

  if (groups.length === 0) return null;

  return (
    <div className={styles['filters']}>
      {groups.map((group) => (
        <div key={group.key} className={styles['group']}>
          <span className={styles['groupLabel']}>{group.label}</span>
          <div className={styles['chips']}>
            {group.values.map((v) => {
              const isActive = activeFilters[group.key] === v.value_id;
              return (
                <button
                  key={v.value_id}
                  type="button"
                  className={cn(styles['chip'], isActive && styles['chipActive'])}
                  onClick={() => onFilterChange(group.key, v.value_id)}
                  aria-pressed={isActive}
                >
                  {v.value_name}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
